import type { RoleName } from '@funkey/shared/permissions';
import type { PortalSwitcher_Data, PortalSwitcher_Option } from './types';

export type PortalSwitcher_TenantRow = {
    tenantId: number;
    type: 'BIZZ' | 'CUSTOMER' | 'PARTNER';
    role: RoleName;
    bizzName: string | null;
    bizzSlug: string | null;
    bizzLogo: string | null;
    bizzBrandingColors: { primary?: string } | null;
    bizzIsTrial: boolean | null;
    partnerId: number | null;
    partnerName: string | null;
};

/** Maps the tenant memberships of a user onto switcher options. */
export function PortalSwitcher_toOptions(
    rows: readonly PortalSwitcher_TenantRow[],
    params: { locale: string; isSuperAdmin: boolean },
): PortalSwitcher_Option[] {
    // A bizz can carry more than one `bizz/logo` media row, which fans the join out into duplicate
    // tenants. Keep the first row per tenant.
    const seenTenantIDs = new Set<number>();
    const options: PortalSwitcher_Option[] = [];

    for (const row of rows) {
        if (seenTenantIDs.has(row.tenantId)) continue;
        seenTenantIDs.add(row.tenantId);

        if (row.type === 'BIZZ' && row.bizzSlug) {
            options.push({
                key: `BIZZ:${row.bizzSlug}`,
                type: 'BIZZ',
                name: row.bizzName ?? row.bizzSlug,
                href: `/${params.locale}/bizz/${row.bizzSlug}`,
                logo: row.bizzLogo,
                brandColor: row.bizzBrandingColors?.primary ?? null,
                role: row.role,
                tenantId: row.tenantId,
                isTrial: row.bizzIsTrial === true,
            });
            continue;
        }

        if (row.type === 'PARTNER' && row.partnerId) {
            options.push({
                key: `PARTNER:${row.partnerId}`,
                type: 'PARTNER',
                name: row.partnerName ?? `#${row.partnerId}`,
                href: `/${params.locale}/partner`,
                logo: null,
                brandColor: null,
                role: row.role,
                tenantId: row.tenantId,
                isTrial: false,
            });
        }
    }

    if (params.isSuperAdmin) {
        options.push({
            key: 'ADMIN',
            type: 'ADMIN',
            name: 'Funkey admin',
            href: `/${params.locale}/admin`,
            logo: null,
            brandColor: null,
            role: null,
            tenantId: null,
            isTrial: false,
        });
    }

    return options;
}

/** First letter of the first two words — the avatar fallback when an organisation has no logo. */
export function PortalSwitcher_initials(name: string) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0])
        .join('');
}

/** Whether the switcher is worth showing: with a single destination it is just a menu of where you
 * already are. Superadmins keep it regardless — they search every business through it. */
export function PortalSwitcher_canSwitch(data: PortalSwitcher_Data) {
    return data.options.length > 1 || data.isSuperAdmin;
}
